import { tr } from "@/lib/tr";

/**
 * Plan feature catalogue — mirrors the real pages of the app, with each report
 * listed on its own. Used by the admin plan editor (checkbox list) and by the
 * runtime plan gate that decides whether a page is included in the workspace plan.
 *
 * Intentionally independent from `app_pages_catalog` / `role_permissions`:
 * role permissions are per-user, this is per-plan.
 */

export interface PlanFeatureEntry {
  /** Stable key stored inside subscription_plans.features */
  key: string;
  /** Group key for display grouping */
  group: string;
  /** Route prefix this key governs (omitted for non-page features) */
  path?: string;
  /**
   * When the plan does not include the key: keep list/report screens readable
   * (and exportable) and only block create/edit routes.
   */
  readOnlyWhenLocked?: boolean;
  label: () => string;
}

export interface PlanFeatureGroup {
  key: string;
  label: () => string;
}

export const PLAN_FEATURE_GROUPS: PlanFeatureGroup[] = [
  { key: "sales", label: () => tr("المبيعات", "Sales", "Ventes", "سیلز") },
  { key: "purchases", label: () => tr("المشتريات", "Purchases", "Achats", "خریداری") },
  { key: "inventory", label: () => tr("المخزون", "Inventory", "Stocks", "انوینٹری") },
  { key: "finance", label: () => tr("المالية", "Finance", "Finance", "فنانس") },
  { key: "assets", label: () => tr("الأصول الثابتة", "Fixed assets", "Immobilisations", "فکسڈ ایسٹس") },
  { key: "contracts", label: () => tr("العقود والمشاريع", "Contracts & projects", "Contrats et projets", "معاہدے اور پروجیکٹس") },
  { key: "reports_financial", label: () => tr("تقارير مالية", "Financial reports", "Rapports financiers", "مالی رپورٹس") },
  { key: "reports_tax", label: () => tr("تقارير الضريبة", "Tax reports", "Rapports fiscaux", "ٹیکس رپورٹس") },
  { key: "reports_sales", label: () => tr("تقارير المبيعات", "Sales reports", "Rapports de ventes", "سیلز رپورٹس") },
  { key: "reports_purchases", label: () => tr("تقارير المشتريات", "Purchase reports", "Rapports d'achats", "خریداری رپورٹس") },
  { key: "reports_inventory", label: () => tr("تقارير المخزون", "Inventory reports", "Rapports de stocks", "انوینٹری رپورٹس") },
  { key: "reports_other", label: () => tr("تقارير أخرى", "Other reports", "Autres rapports", "دیگر رپورٹس") },
  { key: "reports_consolidated", label: () => tr("تقارير مجمعة للشركات", "Consolidated reports", "Rapports consolidés", "مجموعی رپورٹس") },
  { key: "setup", label: () => tr("الإعداد", "Setup", "Configuration", "سیٹ اپ") },
  { key: "settings", label: () => tr("الإعدادات", "Settings", "Paramètres", "سیٹنگز") },
  { key: "other", label: () => tr("أخرى", "Other", "Autres", "دیگر") },
];

const e = (
  key: string,
  group: string,
  label: () => string,
  path?: string,
  readOnlyWhenLocked = false,
): PlanFeatureEntry => ({ key, group, label, path, readOnlyWhenLocked });

export const PLAN_FEATURE_ENTRIES: PlanFeatureEntry[] = [
  // ---- Sales
  e("sales.invoices", "sales", () => tr("فواتير المبيعات", "Sales invoices", "Factures de vente", "سیلز انوائسز"), "/sales/invoices", true),
  e("sales.quotations", "sales", () => tr("عروض الأسعار (مبيعات)", "Sales quotations", "Devis de vente", "سیلز کوٹیشنز"), "/sales/orders", true),
  e("sales.customers", "sales", () => tr("العملاء", "Customers", "Clients", "کسٹمرز"), "/sales/customers", true),

  // ---- Purchases
  e("purchases.invoices", "purchases", () => tr("فواتير المشتريات", "Purchase invoices", "Factures d'achat", "خریداری انوائسز"), "/purchases/invoices", true),
  e("purchases.quotations", "purchases", () => tr("عروض الأسعار (شراء)", "Purchase quotations", "Devis d'achat", "خریداری کوٹیشنز"), "/purchases/orders", true),
  e("purchases.vendors", "purchases", () => tr("الموردون", "Vendors", "Fournisseurs", "وینڈرز"), "/purchases/vendors", true),

  // ---- Inventory
  e("inventory.products", "inventory", () => tr("الأصناف", "Products", "Articles", "پروڈکٹس"), "/sales/products", true),
  e("inventory.issues", "inventory", () => tr("إذون الصرف", "Goods issues", "Bons de sortie", "گڈز اجرا"), "/inventory/issues", true),
  e("inventory.transfers", "inventory", () => tr("التحويلات المخزنية", "Inventory transfers", "Transferts de stock", "انوینٹری ٹرانسفرز"), "/inventory/transfers", true),
  e("inventory.stock_takes", "inventory", () => tr("الجرد الفعلي", "Stock takes", "Inventaires physiques", "اسٹاک ٹیک"), "/inventory/stock-take", true),
  e("inventory.srn", "inventory", () => tr("استلام خدمات", "Service receipts", "Réceptions de services", "سروس رسیدیں"), "/inventory/srn", true),
  e("inventory.damages", "inventory", () => tr("إتلاف المخزون", "Inventory damages", "Casses de stock", "انوینٹری نقصان"), "/inventory/damages", true),
  e("inventory.period_close", "inventory", () => tr("إقفال فترة المخزون", "Inventory period close", "Clôture de période de stock", "انوینٹری پیریڈ کلوز"), "/inventory/period-close", true),
  e("inventory.pending_revaluation", "inventory", () => tr("إعادة التقييم المعلقة", "Pending revaluation", "Réévaluation en attente", "زیر التوا ری ویلیوایشن"), "/inventory/pending-revaluation", true),

  // ---- Finance
  e("journal.entries", "finance", () => tr("قيود اليومية", "Journal entries", "Écritures de journal", "جرنل انٹریز"), "/journal", true),
  e("finance.payment_vouchers", "finance", () => tr("سندات الصرف", "Payment vouchers", "Bons de paiement", "پیمنٹ واؤچرز"), "/vouchers/payment", true),
  e("finance.receipt_vouchers", "finance", () => tr("سندات القبض", "Receipt vouchers", "Bons de recette", "رسید واؤچرز"), "/vouchers/receipt", true),
  e("finance.cash_counts", "finance", () => tr("جرد الخزينة", "Cash counts", "Comptages de caisse", "کیش کاؤنٹس"), "/cash-count", true),
  e("finance.cash_custodies", "finance", () => tr("العهد النقدية", "Cash custodies", "Fonds de caisse", "کیش کسٹڈی"), "/cash-custody", true),
  e("finance.closing_review", "finance", () => tr("مراجعة الإقفال الشهري", "Monthly closing review", "Revue de clôture mensuelle", "ماہانہ کلوزنگ ریویو"), "/closing-review"),

  // ---- Fixed assets
  e("assets.list", "assets", () => tr("سجل الأصول الثابتة", "Fixed assets register", "Registre des immobilisations", "فکسڈ ایسٹس رجسٹر"), "/assets", true),
  e("assets.custody_transfer", "assets", () => tr("نقل حيازة الأصول", "Asset custody transfer", "Transfert de garde d'actifs", "ایسٹ کسٹڈی ٹرانسفر"), "/asset-custody-transfer", true),

  // ---- Contracts & projects
  e("leases.contracts", "contracts", () => tr("عقود الإيجار", "Lease contracts", "Contrats de bail", "لیز معاہدے"), "/leases", true),
  e("expenses.prepaid", "contracts", () => tr("مصاريف مدفوعة مقدماً", "Prepaid expenses", "Charges payées d'avance", "پیشگی اخراجات"), "/prepaid", true),
  e("expenses.accrued", "contracts", () => tr("مصاريف مستحقة", "Accrued expenses", "Charges à payer", "واجب الادا اخراجات"), "/accruals", true),
  e("projects.construction", "contracts", () => tr("مشاريع الإنشاءات", "Construction projects", "Projets de construction", "کنسٹرکشن پروجیکٹس"), "/cip", true),

  // ---- Financial reports (each report separately)
  e("reports.fin.trial_balance", "reports_financial", () => tr("ميزان المراجعة", "Trial balance", "Balance générale", "ٹرائل بیلنس"), "/reports/trial-balance"),
  e("reports.fin.ledger", "reports_financial", () => tr("دفتر الأستاذ", "General ledger", "Grand livre", "جنرل لیجر"), "/reports/ledger"),
  e("reports.fin.income_statement", "reports_financial", () => tr("قائمة الدخل", "Income statement", "Compte de résultat", "انکم اسٹیٹمنٹ"), "/reports/income-statement"),
  e("reports.fin.balance_sheet", "reports_financial", () => tr("الميزانية العمومية", "Balance sheet", "Bilan", "بیلنس شیٹ"), "/reports/balance-sheet"),
  e("reports.fin.cash_flow", "reports_financial", () => tr("قائمة التدفقات النقدية", "Cash flow statement", "Tableau des flux de trésorerie", "کیش فلو اسٹیٹمنٹ"), "/reports/cash-flow"),

  // ---- Tax reports
  e("reports.tax.return", "reports_tax", () => tr("الإقرار الضريبي", "Tax return", "Déclaration de TVA", "ٹیکس ریٹرن"), "/reports/tax-return"),

  // ---- Sales reports
  e("reports.sales.by_item", "reports_sales", () => tr("المبيعات حسب الصنف", "Sales by item", "Ventes par article", "آئٹم کے حساب سے سیلز"), "/reports/sales/by-item"),
  e("reports.sales.by_customer", "reports_sales", () => tr("المبيعات حسب العميل", "Sales by customer", "Ventes par client", "کسٹمر کے حساب سے سیلز"), "/reports/sales/by-customer"),
  e("reports.sales.by_category", "reports_sales", () => tr("المبيعات حسب المجموعة", "Sales by category", "Ventes par catégorie", "کیٹیگری کے حساب سے سیلز"), "/reports/sales/by-category"),
  e("reports.sales.trend", "reports_sales", () => tr("اتجاه المبيعات", "Sales trend", "Tendance des ventes", "سیلز ٹرینڈ"), "/reports/sales/trend"),
  e("reports.sales.ar_aging", "reports_sales", () => tr("أعمار الذمم المدينة", "AR aging", "Balance âgée clients", "اے آر ایجنگ"), "/reports/sales/ar-aging"),
  e("reports.sales.below_cost", "reports_sales", () => tr("البيع بأقل من التكلفة", "Sales below cost", "Ventes sous le coût", "لاگت سے کم سیلز"), "/reports/sales/below-cost"),
  e("reports.sales.lost_customers", "reports_sales", () => tr("العملاء المفقودون", "Lost customers", "Clients perdus", "کھوئے ہوئے کسٹمرز"), "/reports/sales/lost-customers"),
  e("reports.sales.price_history", "reports_sales", () => tr("تاريخ أسعار البيع", "Selling price history", "Historique des prix de vente", "سیلنگ پرائس ہسٹری"), "/reports/sales/price-history"),

  // ---- Purchase reports
  e("reports.purchases.by_item", "reports_purchases", () => tr("المشتريات حسب الصنف", "Purchases by item", "Achats par article", "آئٹم کے حساب سے خریداری"), "/reports/purchases/by-item"),
  e("reports.purchases.price_trend", "reports_purchases", () => tr("اتجاه أسعار الشراء", "Purchase price trend", "Tendance des prix d'achat", "خریداری قیمت کا رجحان"), "/reports/purchases/price-trend"),
  e("reports.purchases.vendor_comparison", "reports_purchases", () => tr("مقارنة الموردين", "Vendor comparison", "Comparaison des fournisseurs", "وینڈر موازنہ"), "/reports/purchases/vendor-comparison"),
  e("reports.purchases.vendor_performance", "reports_purchases", () => tr("أداء الموردين", "Vendor performance", "Performance des fournisseurs", "وینڈر کارکردگی"), "/reports/purchases/vendor-performance"),
  e("reports.purchases.ap_aging", "reports_purchases", () => tr("أعمار الذمم الدائنة", "AP aging", "Balance âgée fournisseurs", "اے پی ایجنگ"), "/reports/purchases/ap-aging"),

  // ---- Inventory reports
  e("reports.inventory.movement", "reports_inventory", () => tr("حركة المخزون", "Inventory movement", "Mouvements de stock", "انوینٹری موومنٹ"), "/reports/inventory/movement"),
  e("reports.inventory.item_card", "reports_inventory", () => tr("بطاقة الصنف", "Item card", "Fiche article", "آئٹم کارڈ"), "/reports/inventory/item-card"),
  e("reports.inventory.valuation", "reports_inventory", () => tr("تقييم المخزون", "Inventory valuation", "Valorisation des stocks", "انوینٹری ویلیوایشن"), "/reports/inventory/valuation"),
  e("reports.inventory.negative_stock", "reports_inventory", () => tr("الأرصدة السالبة", "Negative stock", "Stocks négatifs", "منفی اسٹاک"), "/reports/inventory/negative-stock"),
  e("reports.inventory.aging", "reports_inventory", () => tr("أعمار المخزون", "Inventory aging", "Ancienneté des stocks", "انوینٹری ایجنگ"), "/reports/inventory/aging"),
  e("reports.inventory.slow_moving", "reports_inventory", () => tr("الأصناف بطيئة الحركة", "Slow moving items", "Articles à rotation lente", "سست رفتار آئٹمز"), "/reports/inventory/slow-moving"),
  e("reports.inventory.period_summary", "reports_inventory", () => tr("ملخص فترة المخزون", "Inventory period summary", "Résumé de période de stock", "انوینٹری پیریڈ خلاصہ"), "/reports/inventory/period-summary"),

  // ---- Other reports
  e("reports.assets.register", "reports_other", () => tr("تقرير سجل الأصول", "Assets register report", "Rapport du registre des actifs", "ایسٹس رجسٹر رپورٹ"), "/reports/assets/register"),
  e("reports.leases", "reports_other", () => tr("تقرير الإيجارات", "Leases report", "Rapport des baux", "لیز رپورٹ"), "/reports/leases"),
  e("reports.prepaid", "reports_other", () => tr("تقرير المصاريف المقدمة", "Prepaid expenses report", "Rapport des charges payées d'avance", "پیشگی اخراجات رپورٹ"), "/reports/prepaid"),
  e("reports.analytics", "reports_other", () => tr("التحليلات المالية", "Financial analytics", "Analyses financières", "مالی تجزیات"), "/reports/analytics"),

  // ---- Consolidated reports
  e("reports.consolidated.trial_balance", "reports_consolidated", () => tr("ميزان المراجعة المجمع", "Consolidated trial balance", "Balance consolidée", "مجموعی ٹرائل بیلنس"), "/reports/consolidated/trial-balance"),
  e("reports.consolidated.balance_sheet", "reports_consolidated", () => tr("الميزانية المجمعة", "Consolidated balance sheet", "Bilan consolidé", "مجموعی بیلنس شیٹ"), "/reports/consolidated/balance-sheet"),
  e("reports.consolidated.income_statement", "reports_consolidated", () => tr("قائمة الدخل المجمعة", "Consolidated income statement", "Compte de résultat consolidé", "مجموعی انکم اسٹیٹمنٹ"), "/reports/consolidated/income-statement"),
  e("reports.consolidated.cash_flow", "reports_consolidated", () => tr("التدفقات النقدية المجمعة", "Consolidated cash flow", "Flux de trésorerie consolidés", "مجموعی کیش فلو"), "/reports/consolidated/cash-flow"),

  // ---- Setup
  e("setup.accounts", "setup", () => tr("دليل الحسابات", "Chart of accounts", "Plan comptable", "چارٹ آف اکاؤنٹس"), "/setup/accounts", true),
  e("setup.cost_centers", "setup", () => tr("مراكز التكلفة", "Cost centers", "Centres de coût", "کاسٹ سینٹرز"), "/setup/cost-centers", true),
  e("setup.warehouses", "setup", () => tr("المخازن", "Warehouses", "Entrepôts", "گودام"), "/setup/warehouses", true),
  e("setup.taxes", "setup", () => tr("الضرائب والزكاة", "Taxes and zakat", "Taxes et zakat", "ٹیکس اور زکات"), "/setup/taxes-zakat", true),
  e("setup.numbering", "setup", () => tr("الترقيم التسلسلي", "Document numbering", "Numérotation des documents", "دستاویز نمبرنگ"), "/setup/document-numbering", true),
  e("setup.fiscal_periods", "setup", () => tr("الفترات المالية", "Fiscal periods", "Périodes fiscales", "فسکل پیریڈز"), "/setup/fiscal-periods", true),
  e("setup.product_categories", "setup", () => tr("مجموعات الأصناف", "Product categories", "Catégories d'articles", "پروڈکٹ کیٹیگریز"), "/setup/product-categories", true),
  e("setup.asset_groups", "setup", () => tr("مجموعات الأصول", "Asset groups", "Groupes d'actifs", "ایسٹ گروپس"), "/setup/asset-groups", true),
  e("setup.damage_reasons", "setup", () => tr("أسباب الإتلاف", "Damage reasons", "Motifs de casse", "نقصان کی وجوہات"), "/setup/damage-reasons", true),
  e("setup.cash_denominations", "setup", () => tr("فئات الأوراق النقدية", "Cash denominations", "Coupures de caisse", "کرنسی ڈینومینیشنز"), "/setup/cash-denominations", true),

  // ---- Settings
  e("settings.company", "settings", () => tr("إعدادات الشركة", "Company settings", "Paramètres de la société", "کمپنی سیٹنگز"), "/settings/company", true),
  e("settings.members", "settings", () => tr("الأعضاء", "Members", "Membres", "ممبرز"), "/settings/members", true),
  e("settings.roles", "settings", () => tr("الأدوار والصلاحيات", "Roles and permissions", "Rôles et autorisations", "رولز اور اجازتیں"), "/settings/roles", true),
  e("settings.ai", "settings", () => tr("إعدادات الذكاء الاصطناعي", "AI settings", "Paramètres IA", "اے آئی سیٹنگز"), "/settings/ai", true),

  // ---- Other
  e("ai_advisor", "other", () => tr("المستشار الذكي", "AI advisor", "Conseiller IA", "اے آئی مشیر"), "/ai"),
  e("multi_company", "other", () => tr("تعدد الشركات", "Multiple companies", "Multi-sociétés", "متعدد کمپنیاں")),
  e("priority_support", "other", () => tr("دعم فني بأولوية", "Priority support", "Support prioritaire", "ترجیحی سپورٹ")),
];

export const PLAN_FEATURE_KEYS = PLAN_FEATURE_ENTRIES.map((x) => x.key);

/**
 * Old marketing-style keys still stored on existing plans, expanded to the new
 * page keys so no current subscriber loses access before an admin edits the plan.
 */
export const LEGACY_FEATURE_MAP: Record<string, string[]> = {
  chart_of_accounts: ["setup.accounts"],
  journals: ["journal.entries", "finance.payment_vouchers", "finance.receipt_vouchers"],
  vouchers: ["finance.payment_vouchers", "finance.receipt_vouchers"],
  sales_invoices: ["sales.invoices", "sales.customers", "sales.returns"],
  purchase_invoices: ["purchases.invoices", "purchases.vendors", "purchases.returns"],
  price_quotes: ["sales.quotations", "purchases.quotations"],
  lease_contracts: ["leases.contracts", "expenses.prepaid", "expenses.accrued", "reports.leases", "reports.prepaid"],
  prepaid_accrued_expenses: ["expenses.prepaid", "expenses.accrued", "reports.prepaid"],
  cost_centers: ["setup.cost_centers"],
  multi_user: ["settings.members", "settings.roles"],
  inventory: [
    "inventory.products", "inventory.receipts", "inventory.issues", "inventory.transfers",
    "inventory.stock_takes", "inventory.srn", "inventory.damages",
    "inventory.period_close", "inventory.pending_revaluation",
    "reports.inventory.movement", "reports.inventory.item_card", "reports.inventory.valuation",
    "reports.inventory.negative_stock", "reports.inventory.aging",
    "reports.inventory.slow_moving", "reports.inventory.period_summary",
  ],
  fixed_assets: [
    "assets.list", "assets.vouchers", "assets.disposals", "assets.custody_transfer",
    "setup.asset_groups", "reports.assets.register",
  ],
  ai_advisor: ["ai_advisor", "settings.ai"],
  priority_support: ["priority_support"],
  multi_company: ["multi_company"],
  "closing.checklist": ["finance.closing_review"],
  "reports.basic": [
    "reports.fin.trial_balance", "reports.fin.ledger", "reports.fin.income_statement",
    "reports.fin.balance_sheet", "reports.fin.cash_flow", "reports.tax.return",
  ],
  "reports.advanced": [
    "reports.analytics",
    "reports.sales.by_item", "reports.sales.by_customer", "reports.sales.by_category",
    "reports.sales.trend", "reports.sales.ar_aging", "reports.sales.below_cost",
    "reports.sales.lost_customers", "reports.sales.price_history",
    "reports.purchases.by_item", "reports.purchases.price_trend",
    "reports.purchases.vendor_comparison", "reports.purchases.vendor_performance",
    "reports.purchases.ap_aging",
    "reports.leases", "reports.prepaid",
  ],
  "reports.consolidated": [
    "reports.consolidated.trial_balance", "reports.consolidated.balance_sheet",
    "reports.consolidated.income_statement", "reports.consolidated.cash_flow",
  ],
};

/**
 * Core keys every paying/trialing workspace keeps regardless of the plan.
 * These are structural screens, not sellable modules.
 */
export const ALWAYS_ALLOWED_KEYS = new Set<string>([
  "setup.accounts", "setup.cost_centers", "setup.taxes", "setup.numbering",
  "setup.fiscal_periods", "setup.warehouses", "setup.product_categories",
  "setup.asset_groups", "setup.damage_reasons", "setup.cash_denominations",
  "settings.company", "settings.members", "settings.roles", "settings.ai",
  "journal.entries", "finance.payment_vouchers", "finance.receipt_vouchers",
  "finance.cash_counts", "finance.cash_custodies",
  "sales.invoices", "sales.customers", "sales.returns", "sales.quotations",
  "purchases.invoices", "purchases.vendors", "purchases.returns", "purchases.quotations",
  "projects.construction",
  "reports.fin.trial_balance", "reports.fin.ledger", "reports.fin.income_statement",
  "reports.fin.balance_sheet", "reports.fin.cash_flow", "reports.tax.return",
]);

/** Expands stored plan features (new + legacy keys) into the effective key set. */
export function expandPlanFeatures(features: unknown): Set<string> {
  const out = new Set<string>(ALWAYS_ALLOWED_KEYS);
  if (Array.isArray(features)) {
    for (const f of features) {
      if (typeof f !== "string") continue;
      out.add(f);
      for (const mapped of LEGACY_FEATURE_MAP[f] ?? []) out.add(mapped);
    }
  }
  return out;
}

/** Longest-prefix-first route → plan key map. */
const PLAN_KEY_PREFIXES: Array<[string, PlanFeatureEntry]> = PLAN_FEATURE_ENTRIES
  .filter((x) => !!x.path)
  .map((x) => [x.path as string, x] as [string, PlanFeatureEntry])
  .sort((a, b) => b[0].length - a[0].length);

export function getPlanEntryForPath(pathname: string): PlanFeatureEntry | null {
  for (const [prefix, entry] of PLAN_KEY_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return entry;
  }
  return null;
}

const ID_ROUTE_IS_WRITE = new Set<string>([
  "sales.customers", "purchases.vendors", "inventory.products",
  "inventory.issues", "inventory.transfers", "inventory.stock_takes",
  "inventory.srn", "inventory.damages", "finance.cash_counts",
  "setup.warehouses", "setup.damage_reasons",
]);

/** Identifies create/edit routes while leaving list, detail and export screens readable. */
export function isPlanWriteRoute(pathname: string, entry: PlanFeatureEntry): boolean {
  const normalized = pathname.replace(/\/+$/, "");
  const last = normalized.split("/").pop() ?? "";
  if (last === "new" || last === "edit" || normalized.includes("/new/")) return true;
  if (!entry.path || !ID_ROUTE_IS_WRITE.has(entry.key)) return false;
  const remainder = normalized.slice(entry.path.length).replace(/^\/+/, "");
  return remainder.length > 0 && !remainder.includes("/");
}

export function planFeatureEntryLabel(key: string): string | null {
  const entry = PLAN_FEATURE_ENTRIES.find((x) => x.key === key);
  return entry ? entry.label() : null;
}
